import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { Suspense, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AssetHierarchyTabs } from '@/components/features/settings/assets/AssetHierarchyTabs';
import { MobileTabChips } from '@/components/shared/mobile-tab-chips';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/use-mobile';

const HIERARCHY_TABS = [
  { value: 'sites', labelKey: 'assets:tabs.sites' },
  { value: 'areas', labelKey: 'assets:tabs.areas' },
  { value: 'machines', labelKey: 'assets:tabs.machines' },
  { value: 'sub-assets', labelKey: 'assets:tabs.subAssets' },
  { value: 'components', labelKey: 'assets:tabs.components' },
  { value: 'hierarchy', labelKey: 'assets:tabs.hierarchyView' },
] as const;

function AssetManagementPageLoading() {
  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-96" />
      </div>
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <div className="space-y-4">
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/asset-management')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      hierarchyTab: (search.hierarchyTab as string) || 'sites',
    };
  },
  pendingComponent: AssetManagementPageLoading,
  component: () => (
    <Suspense fallback={<AssetManagementPageLoading />}>
      <AssetManagementPage />
    </Suspense>
  ),
});

function AssetManagementPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hierarchyTab } = useSearch({ from: '/_authenticated/asset-management' });
  const [, setRefreshTrigger] = useState(0);
  const isMobile = useIsMobile();

  const activeTab = hierarchyTab || 'sites';

  const refreshAssetData = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleTabChange = (value: string) => {
    navigate({
      to: '/asset-management',
      search: { hierarchyTab: value },
    });
  };

  if (isMobile) {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight">{t('assetManagement.title')}</h1>
          <p className="text-xs text-muted-foreground">{t('assetManagement.description')}</p>
        </div>

        <MobileTabChips
          tabs={HIERARCHY_TABS.map((tab) => ({
            value: tab.value,
            label: t(tab.labelKey),
          }))}
          value={activeTab}
          onChange={handleTabChange}
        />

        <AssetHierarchyTabs
          activeTab={activeTab}
          onSubTabChange={handleTabChange}
          onDataChange={refreshAssetData}
          mobileMode
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
          {t('assetManagement.title')}
        </h1>
        <p className="text-xs sm:text-sm text-muted-foreground">
          {t('assetManagement.description')}
        </p>
      </div>

      <AssetHierarchyTabs
        activeTab={activeTab}
        onSubTabChange={handleTabChange}
        onDataChange={refreshAssetData}
      />
    </div>
  );
}
